import React from "react";
import { Link } from "react-router-dom";
import { FileText, BookOpen, FolderOpen, ArrowRight } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";

const governanceLinks = [
  {
    title: "Declaration",
    description:
      "The public declaration of Yunah'Shach'Qal Yashar'al, its purpose, mission, and covenant witness before YaHUaH.",
    href: "/governance/declaration",
    icon: FileText,
  },
  {
    title: "Constitution & Internal Covenant",
    description:
      "The governing covenant, national order, and standards of stewardship rooted in Turah.",
    href: "/governance/constitution",
    icon: BookOpen,
  },
  {
    title: "Official Documents Library",
    description:
      "Access official documents, records, downloads, and public governance materials.",
    href: "/governance/documents",
    icon: FolderOpen,
  },
];

export default function Governance() {
  return (
    <div className="min-h-screen" data-testid="governance-page">
      {/* Hero */}
      <section className="bg-royal-blue text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-kingdom-gold text-sm font-semibold uppercase tracking-wider">
              Governance
            </span>

            <h1 className="heading-hero mt-2 mb-6">
              Covenant Order & Documents
            </h1>

            <p className="body-large text-slate-300">
              Foundational documents, covenant standards, and public records for
              Yunah'Shach'Qal Yashar'al.
            </p>
          </div>
        </div>
      </section>

      {/* Document Links */}
      <section className="bg-parchment py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {governanceLinks.map((item) => (
              <Link key={item.href} to={item.href} className="block group">
                <Card className="card-official h-full hover:shadow-xl transition-shadow">
                  <CardHeader>
                    <div className="w-12 h-12 bg-kingdom-gold/10 rounded-full flex items-center justify-center mb-4">
                      <item.icon className="w-6 h-6 text-kingdom-gold" />
                    </div>

                    <CardTitle className="heading-card text-royal-blue group-hover:text-kingdom-gold transition-colors flex items-center gap-2">
                      {item.title}
                      <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </CardTitle>
                  </CardHeader>

                  <CardContent>
                    <CardDescription className="text-slate-600 text-base">
                      {item.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Governance Overview */}
      <section className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-section text-royal-blue text-center mb-8">
            Governance Structure
          </h2>

          <div className="prose prose-lg max-w-none text-slate-700">
            <p>
              Yunah'Shach'Qal Yashar'al is ordered as a covenantal nation under
              YaHUaH, not as a man-centered monarchy, political corporation, or
              party system. Its governance is rooted in Turah, righteous
              judgment, household responsibility, elder counsel, appointed
              stewardship, and service to the people.
            </p>

            <p>
              A position is not treated as unchecked rule over the people. Those
              entrusted with oversight are expected to serve with humility,
              wisdom, accountability, and obedience to the Instructions of
              YaHUaH. Major matters are weighed through council, elders, judges,
              heads of households, and lawful witnesses according to Turah.
            </p>

            <p>
              The purpose of this order is to preserve righteousness, protect
              households, strengthen community responsibility, resolve disputes
              with upright judgment, and prepare a people walking in obedience
              before YaHUaH.
            </p>

            <h3 className="font-heading text-xl text-royal-blue">
              Covenant Governance Principles
            </h3>

            <ul>
              <li>YaHUaH alone is recognized as Supreme Lawgiver and Judge.</li>
              <li>Turah is the highest standard for national order.</li>
              <li>Guardians and stewards serve under accountability.</li>
              <li>Elders and council provide counsel, discernment, and correction.</li>
              <li>Judges weigh disputes according to Turah and lawful witnesses.</li>
              <li>Households remain responsible for instruction, care, and provision.</li>
              <li>Communities retain local stewardship while walking in shared covenant order.</li>
              <li>No office or appointment is based on self-exaltation, wealth, popularity, or coercion.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Foundation */}
      <section className="bg-royal-blue text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-section text-center mb-8">Our Foundation</h2>

          <div className="bg-slate-800/50 p-8 rounded-sm">
            <p className="text-slate-300 leading-relaxed">
              The foundation of Yunah'Shach'Qal Yashar'al is YaHUaH, Maker of
              Ha'Shamaym, Aratz, and Yum. We acknowledge YaHUSHA as Mashyach and
              Kinsman Redeemer, and the Ruch Ha'Qadash as the guide, teacher,
              and witness given by YaHUaH. The nation exists to call the
              scattered ones back to the Ancient Path and to walk according to
              the Instructions of YaHUaH.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
